import {
  type KilnHeatConsumptionsResponse,
  type KpiPfyPayload,
  type MtmYtmRequestPayload,
  type PlannedYearlyKilnHeatConsumptionsHierarchy,
  PERFORMANCE_KPI_REQUEST_STALE_TIME
} from '@domain/kpis'
import {ApiClient} from '@hconnect/apiclient'
import {useUpmPlantsContext} from '@hconnect/upm'
import {useQueries, UseQueryResult} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useCallback, useMemo} from 'react'

import {mapKilnHeatConsumptionsDto} from '../../mappers'
import {type KpiHookReturn} from '../../types'

import {
  getKilnHeatConsumption,
  getKilnHeatConsumptionMtmYtmQueryKeys
} from './useKilnHeatConsumption'

const getKilnHeatConsumptionPfy = async (payload?: KpiPfyPayload, signal?: AbortSignal) => {
  const url = '/kpi/dashboard/kiln-heat-consumptions-pfy'
  const response = await ApiClient.axiosInstance.get<PlannedYearlyKilnHeatConsumptionsHierarchy>(
    url,
    {
      params: {...payload},
      signal
    }
  )

  return response.data
}

export const useKilnHeatConsumptionData = (
  payload: MtmYtmRequestPayload,
  enabled = true
): KpiHookReturn => {
  const upmPlants = useUpmPlantsContext()

  const queries = useQueries<
    [
      UseQueryResult<PlannedYearlyKilnHeatConsumptionsHierarchy, AxiosError>,
      UseQueryResult<KilnHeatConsumptionsResponse, AxiosError>
    ]
  >({
    queries: [
      {
        queryKey: [
          'kiln-heat-consumptions-pfy',
          {locationId: payload?.locationId, year: payload?.year}
        ],
        queryFn: ({signal}) =>
          getKilnHeatConsumptionPfy(
            {
              locationId: payload?.locationId,
              year: payload?.year
            },
            signal
          ),
        enabled,
        staleTime: PERFORMANCE_KPI_REQUEST_STALE_TIME
      },
      {
        queryKey: getKilnHeatConsumptionMtmYtmQueryKeys(payload),
        queryFn: ({signal}) => getKilnHeatConsumption(payload, signal),
        enabled,
        staleTime: PERFORMANCE_KPI_REQUEST_STALE_TIME
      }
    ]
  })

  const refetch = useCallback(() => {
    queries.forEach(({error, refetch: queryRefetch}) => {
      if (error) {
        void queryRefetch()
      }
    })
  }, [queries])

  const [pfyQuery, dataQuery] = queries

  const kilnHeatConsumption = useMemo(() => {
    const pfyData = pfyQuery.data
    const data = dataQuery.data
    return pfyData && data ? mapKilnHeatConsumptionsDto(data, upmPlants, pfyData) : undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(pfyQuery.data), JSON.stringify(dataQuery.data)])

  return {
    data: kilnHeatConsumption,
    isInitialLoading: pfyQuery.isInitialLoading || dataQuery.isInitialLoading,
    error: pfyQuery.error || dataQuery.error,
    refetch
  }
}
